import { PropsWithChildren, useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AxiosError } from 'axios'
import { AttachmentIcon, Button, Divider, H, P, Wrapper } from '@farewill/ui'
import { COLOR, FONT, GTR } from '@farewill/ui/tokens'
import {
  fetchFuneralPlanDocuments,
  generateFuneralPlanDocuments,
  showModal,
} from 'state/actions'
import { Event } from 'lib/models/event'
import { formatToHuman, getTimeDescriptionFromTimestamp } from 'utils/helpers'
import { formatApiError } from 'lib/formatting/errors'
import ActivityTimestamp from 'components/activity-timestamp'
import MessageBox from 'components/message-box'
import { Download } from './documents-download'
import { MAP_BRAND_ID_TO_NAME } from 'config/brands'
import { FuneralPlan } from 'lib/models/funeral-plan'
import { FUNERAL_PLAN_CUSTOM_DOCUMENTS } from './constants'
import GenerateDocumentsModal from './generate-documents-modal'
import type { DocumentRecipientType } from './generate-documents-modal'

const StyledDocumentRow = styled.ul`
  align-items: center;
  display: grid;
  font-size: ${FONT.SIZE.XS};
  grid-template-columns: 60px 1fr;
  margin-bottom: ${GTR.XS};
  padding: ${GTR.XS} 0;
  width: 100%;
`

const StyledIconWrapper = styled.div`
  position: relative;
`

const StyledIcon = styled.div`
  background-color: white;
  border-radius: 3px;
  border: 2px solid ${COLOR.GREY.LIGHT};
  height: 46px;
  text-decoration: none;
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:not(:first-child) {
    left: 3px;
    position: absolute;
    top: -3px;
    z-index: -1;
  }
`

const documentLinkStyles = css`
  display: inline-block;
  font-size: ${FONT.SIZE.M};
  line-height: 1;
  margin-bottom: ${GTR.XXS};
`

const StyledExternalDocumentLink = styled(Button.Underline)`
  ${documentLinkStyles}
  padding-top: 0;
  padding-bottom: ${GTR.XXS};
`

const StyledActivityTimestamp = styled(ActivityTimestamp)`
  color: ${COLOR.GREY.DARK};
  font-size: ${FONT.SIZE.XS};
`

const getMostRecentEventDate = (events: Event[]): string | undefined => {
  if (events.length === 0) return undefined

  return events.reduce((mostRecent, event) => {
    const eventDate = event.attributes.createdAt
    return !mostRecent || new Date(eventDate) > new Date(mostRecent)
      ? eventDate
      : mostRecent
  }, events[0].attributes.createdAt)
}

/**
 * The backend returns all documents_generated events for the funeral plan (via the fetchFuneralPlanDocuments action)
 * We only want to display the most recently generated document for each document type.
 * To obtain the most recently generated document:
 * 1. flatmap to get the titles (because there can be multiple titles in each documents_generated event), adding the createdAt date to the returned array
 * 2. sort by createdAt (newest first)
 * 3. iterate over the possible document types and find the first (most recent) document generated
 *
 * Earlier versions of each document are kept too: every render is saved to S3 under its own key, and
 * each documents_generated event lists the keys it saved in metadata.documents. We only show the newest
 * on purpose, and downloading it uses the newest event's key (getDownloadLink on the server).
 * See documentation/pdf-generation.md.
 **/
const getDocumentsToDisplay = (
  documentsGeneratedEvents: Event[],
  brandId: string
) => {
  const documentsGeneratedTitlesSortedByDate = documentsGeneratedEvents
    .flatMap((doc) =>
      (doc.attributes?.metadata?.titles || []).map((title) => ({
        title,
        createdAt: doc.attributes.createdAt,
      }))
    )
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )

  const brandedDocumentTypes =
    FUNERAL_PLAN_CUSTOM_DOCUMENTS[
      MAP_BRAND_ID_TO_NAME[
        brandId
      ].toLowerCase() as keyof typeof FUNERAL_PLAN_CUSTOM_DOCUMENTS
    ]

  return Object.values(brandedDocumentTypes).flatMap((documentType) => {
    const generatedDocument = documentsGeneratedTitlesSortedByDate.find(
      (doc) => doc.title === documentType
    )
    return generatedDocument ? [generatedDocument] : []
  })
}

const getLastDocumentsMessage = (lastGeneratedAt?: string) => {
  if (!lastGeneratedAt) {
    return 'No print request has been sent yet'
  }

  const timeDescription = getTimeDescriptionFromTimestamp(lastGeneratedAt, true)
  const isGeneratedInThePast24Hours =
    new Date(lastGeneratedAt) > new Date(Date.now() - 24 * 60 * 60 * 1000)

  if (isGeneratedInThePast24Hours) {
    return `Print request last generated ${timeDescription}. If you generate a new print request, any previous orders that have not been dispatched will be cancelled and replaced by the new request.`
  }

  return `Print request last generated ${timeDescription}.`
}

type SectionWrapperProps = PropsWithChildren<{
  handleGenerateDocuments?: () => void
  isGeneratingDocuments?: boolean
  lastDocumentsMessage?: string
}>

const SectionWrapper = ({
  children,
  handleGenerateDocuments,
  isGeneratingDocuments,
  lastDocumentsMessage,
}: SectionWrapperProps) => (
  <div>
    <H size="M">Documents</H>
    {children}
    <>
      {handleGenerateDocuments && (
        <Wrapper margin={['M', 0]}>
          <Divider />
          <MessageBox margin={['M', 0]}>
            If something is missing, or details have changed you can post a new
            set of documents. This will send a print and post request to
            PurePrint, so please check all the plan details are correct before
            continuing.
          </MessageBox>
          {lastDocumentsMessage && <P size="S">{lastDocumentsMessage}</P>}
          <Button.Primary
            onClick={handleGenerateDocuments}
            loading={isGeneratingDocuments}
          >
            Post documents
          </Button.Primary>
        </Wrapper>
      )}
    </>
  </div>
)

const Documents = () => {
  const { id: funeralPlanId } = useParams<{ id: string }>()
  const [showDownloadIframe, setShowDownloadIframe] = useState<boolean>(false)
  const [fileTitle, setFileTitle] = useState<string>()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const dispatch = useDispatch()
  const documentsGeneratedEvents = useSelector(
    (state: { funeralPlanDocuments: Event[] }) => state.funeralPlanDocuments
  )

  const funeralPlan = useSelector(
    (state: { funeralPlanCase: FuneralPlan }) => state.funeralPlanCase
  )
  const { purchaser, nominatedRepresentative } = funeralPlan.attributes
  const brandId = purchaser.brandId

  const documentsToDisplay = getDocumentsToDisplay(
    documentsGeneratedEvents,
    brandId
  )

  const lastGeneratedAt = getMostRecentEventDate(documentsGeneratedEvents)
  const lastDocumentsMessage = getLastDocumentsMessage(lastGeneratedAt)

  const sendNominatedRepDocumentsSeparately =
    nominatedRepresentative?.canBeContactedDirectly

  useEffect(() => {
    dispatch(fetchFuneralPlanDocuments(funeralPlanId))
  }, [dispatch, funeralPlanId])

  const triggerDocumentDownload = (title: string) => {
    // We can ensure every button click triggers a new download, even if the
    // title stays the same, by removing and remounting the
    // download iframe. Because there is no reliable way to check if a document
    // download is complete, we can hide the download iframe on a new button
    // click and then trigger the remounting with the useEffect hook below.
    setShowDownloadIframe(false)
    setFileTitle(title)
  }

  const handleGenerateDocuments = () => {
    dispatch(
      showModal({
        component: GenerateDocumentsModal,
        headingText: 'You are about to post documents',
        config: {
          purchaserName: `${purchaser.firstName} ${purchaser.lastName}`.trim(),
          purchaserAddress: purchaser.addresses?.[0],
          nominatedRepresentativeName: sendNominatedRepDocumentsSeparately
            ? `${nominatedRepresentative.firstName} ${nominatedRepresentative.lastName}`.trim()
            : undefined,
          nominatedRepresentativeAddress: sendNominatedRepDocumentsSeparately
            ? nominatedRepresentative.addressFields
            : undefined,
          sendNominatedRepDocumentsSeparately,
          confirmCallback: async (recipientType: DocumentRecipientType) => {
            setIsLoading(true)
            try {
              await dispatch(
                generateFuneralPlanDocuments(funeralPlanId, recipientType)
              )
              dispatch(fetchFuneralPlanDocuments(funeralPlanId))
            } catch (error) {
              const errors =
                (
                  error as AxiosError<{
                    errors?: (string | { detail: string })[]
                  }>
                ).response?.data?.errors || []
              const message = errors.length
                ? errors.map(formatApiError).join(', ')
                : 'Error posting documents'
              toast(message, { toastId: 'generate-documents-error' })
              throw error
            } finally {
              setIsLoading(false)
            }
          },
        },
      })
    )
  }

  useEffect(() => {
    if (!showDownloadIframe) {
      setShowDownloadIframe(true)
    }
  }, [showDownloadIframe])

  if (isLoading) {
    return <SectionWrapper>Loading...</SectionWrapper>
  }

  if (documentsToDisplay.length === 0) {
    return (
      <SectionWrapper
        handleGenerateDocuments={handleGenerateDocuments}
        isGeneratingDocuments={isLoading}
        lastDocumentsMessage={lastDocumentsMessage}
      >
        <Wrapper>
          <P>No documents have been posted yet</P>
        </Wrapper>
      </SectionWrapper>
    )
  }

  return (
    <SectionWrapper
      handleGenerateDocuments={handleGenerateDocuments}
      isGeneratingDocuments={isLoading}
      lastDocumentsMessage={lastDocumentsMessage}
    >
      {documentsToDisplay.map(
        ({ title, createdAt }, index) =>
          title && (
            <StyledDocumentRow key={`${title}-${createdAt}-${index}`}>
              <StyledIconWrapper>
                <StyledIcon>
                  <AttachmentIcon color={COLOR.GREY.LIGHT} />
                </StyledIcon>
              </StyledIconWrapper>
              <div>
                <StyledExternalDocumentLink
                  flush
                  onClick={() => triggerDocumentDownload(title)}
                >
                  {formatToHuman(title)}
                </StyledExternalDocumentLink>
                <div>
                  Added <StyledActivityTimestamp datetime={createdAt} />
                </div>
              </div>
            </StyledDocumentRow>
          )
      )}
      {showDownloadIframe && fileTitle && <Download title={fileTitle} />}
    </SectionWrapper>
  )
}

export default Documents
